import {
    useMotionTemplate,
    useMotionValue,
    useMotionValueEvent,
    useScroll,
    useSpring,
    useTransform,
} from "motion/react";
import { useCallback, useRef, useState } from "react";

export function useSpotlightEffect() {
    const [ref, setRef, stopPoints] = useMeasureRef();
    const [isVisible, setIsVisible] = useState(false);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["end start", "start end"],
    });

    useMotionValueEvent(scrollYProgress, "change", (latest) => {
        if (latest >= 1 || latest <= 0) setIsVisible(false);
        else setIsVisible(true);
    });

    const scrollValueX = useTransform(
        scrollYProgress,
        [0, 0.25, 0.5, 0.75, 1],
        [stopPoints.leftX, stopPoints.topX, stopPoints.rightX, stopPoints.bottomX, stopPoints.leftX],
    );

    const scrollValueY = useTransform(
        scrollYProgress,
        [0, 0.25, 0.5, 0.75, 1],
        [stopPoints.leftY, stopPoints.topY, stopPoints.rightY, stopPoints.bottomY, stopPoints.leftY],
    );

    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    const scrolLValueXSmooth = useSpring(scrollValueX, { damping: 15, mass: 1, stiffness: 100 });
    const scrolLValueYSmooth = useSpring(scrollValueY, { damping: 15, mass: 1, stiffness: 100 });

    const combinedX = useTransform(() => {
        if (mouseX.get() === 0) return scrolLValueXSmooth.get();
        return mouseX.get();
    });

    const combinedY = useTransform(() => {
        if (mouseY.get() === 0) return scrolLValueYSmooth.get();
        return mouseY.get();
    });

    const combinedXSpring = useSpring(combinedX, { damping: 10, mass: 0.01, stiffness: 200 });

    const combinedYSpring = useSpring(combinedY, { damping: 10, mass: 0.01, stiffness: 200 });

    const maskImage = useMotionTemplate`radial-gradient(200px at ${combinedXSpring}px ${combinedYSpring}px, white, transparent)`;
    const style = { maskImage, WebkitMaskImage: maskImage };

    const onMouseMove = useCallback(
        ({ currentTarget, clientX, clientY }: { currentTarget: HTMLElement; clientX: number; clientY: number }) => {
            const { left, top } = currentTarget.getBoundingClientRect();
            const mouseXNew = clientX - left;
            const mouseYNew = clientY - top;
            mouseX.set(mouseXNew);
            mouseY.set(mouseYNew);
        },
        [mouseX, mouseY],
    );

    const onMouseLeave = useCallback(() => {
        mouseX.set(0);
        mouseY.set(0);
    }, [mouseX, mouseY]);

    return {
        isVisible,
        setRef,
        style,
        onMouseMove,
        onMouseLeave,
        change: combinedYSpring,
    };
}

function useMeasureRef() {
    const ref = useRef<HTMLDivElement | null>(null);
    const [stopPoints, setStopPoints] = useState({
        leftX: 0,
        leftY: 0,
        topX: 0,
        topY: 0,
        rightX: 0,
        rightY: 0,
        bottomX: 0,
        bottomY: 0,
    });
    const setRef = useCallback((node: HTMLDivElement | null) => {
        if (ref.current) {
            // Make sure to cleanup any events/references added to the last instance
        }

        if (node) {
            // Check if a node is actually passed. Otherwise node would be null.
            // You can now do what you need to, addEventListeners, measure, etc.
            const { width, height } = node.getBoundingClientRect();
            const stopPoints = {
                leftX: width * 0.25,
                leftY: height * 0.5,
                topX: width * 0.5,
                topY: height * 0.25,
                rightX: width * 0.75,
                rightY: height * 0.5,
                bottomX: width * 0.5,
                bottomY: height * 0.75,
            };
            setStopPoints(stopPoints);
        }

        // Save a reference to the node
        ref.current = node;
    }, []);

    return [ref, setRef, stopPoints] as const;
}
